import Cookies from 'js-cookie';
import api from './apiClient';
import {config} from 'config/config';
import {Modal} from 'antd';

// Fallback lifetime used only when the server response carries no expiry.
const DEFAULT_TOKEN_LIFETIME = 86400;

// The token is read from JS and sent in the Authorization header, so SameSite=Strict costs nothing
// and keeps the cookie out of cross-site requests. Secure is conditional: the local stack runs on
// plain http, where a secure cookie would never be stored at all.
const cookieAttributes = (expires: Date) => ({
  expires: expires,
  sameSite: 'strict' as const,
  secure: window.location.protocol === 'https:',
});

const tokenLifetime = (response: any): number => {
  const value = Number(response?.expires_in ?? response?.expiresIn);

  return Number.isFinite(value) && value > 0 ? value : DEFAULT_TOKEN_LIFETIME;
};

const set = async (token: any, expires: any) => {
  const lifetime = Number(expires) > 0 ? Number(expires) : DEFAULT_TOKEN_LIFETIME;
  const expirationDate = new Date(new Date().getTime() + lifetime * 1000);
  Cookies.set('jwt', token, cookieAttributes(expirationDate));
};

const get = () => {
  return Cookies.get('jwt');
};

const getHeader = () => {
  return 'Bearer ' + get();
};

const isTokenActive = () => {
  return get() ? true : false
};

const remove = () => {
  Cookies.remove('jwt');
};

const refresh = async () => {
  const response = await api.request(config.api.routes.auth.refresh, {}, 'POST', false);
  const token = response.token;
  await set(token, tokenLifetime(response));
};

const login = async (login: string, password: string, match: any, countryCode: string | null) => {
  const response = await api.request(config.api.routes.auth.login, {login: login, password: password, connection: match?.params.uuid, countryCode: countryCode}, 'POST');

  if (response.hasOwnProperty('error') && response.code !== 200) {
    throw new Error(response.error)
  }

  const token = response.token;
  await set(token, tokenLifetime(response));
};

const logout = async (session: any = null) => {
  const response = await api.request(config.api.routes.auth.logout, {}, 'POST');
  sessionStorage.removeItem('current');
  sessionStorage.removeItem('translations');
  sessionStorage.removeItem('translations_public');
  sessionStorage.removeItem('translations_full');
  localStorage.setItem('locale', config.defaultEnvironmentLocale);

  // Signal the other tabs, exactly as switchUserRole does — without this a logout here left the
  // other tabs rendering an authenticated UI until their next request happened to fail.
  localStorage.setItem('jwt_token_changed', Date.now().toString());

  await remove();
  if (!session && response?.url) {
    window.location.replace(response.url);
  } else {
    window.location.reload();
  }
};

const refreshRole = async (userTypeId: any) => {
  let switchUserRoleResponse = await api.request(config.api.routes.auth.switchUserRole, {}, 'POST', true, false, {userTypeId});
  await set(switchUserRoleResponse.token, tokenLifetime(switchUserRoleResponse));
}


const switchUserRole = async (userTypeId: any) => {
  let switchUserRoleResponse = await api.request(config.api.routes.auth.switchUserRole, {}, 'POST', true, false, {userTypeId});

  if (switchUserRoleResponse.token) {
    await remove();

    await set(switchUserRoleResponse.token, tokenLifetime(switchUserRoleResponse));

    localStorage.setItem('jwt_token_changed', Date.now().toString());

    if (window.location.pathname === '/403') {
      window.location.assign('/')
    } else {
      window.location.reload();
    }
  } else {
    let errorMessage = 'switch_to_user_role_failed'

    if (switchUserRoleResponse.error === 'customer_has_no_platform_access') {
      errorMessage = 'users.' + switchUserRoleResponse.error
    }
    if (switchUserRoleResponse.error === 'exceeded_max_allowed_students_count') {
      errorMessage = 'error.' + switchUserRoleResponse.error
    }

    Modal.error({
      title: 'Error',
      content: errorMessage,
      onOk:() => window.location.reload()
    });
  }
}

const switchUser = async (userId: any, userTypeId: any) => {
    let switchUserResponse = await api.request('/api/v1/auth/users/' + userId + '/switch-to-user/' + userTypeId, {}, 'POST', true, false, {});

    if (switchUserResponse.token) {
      await remove();

      await set(switchUserResponse.token, tokenLifetime(switchUserResponse));

      localStorage.setItem('jwt_token_changed', Date.now().toString());

      return true
    } else {
      console.error(switchUserResponse.error);

      return false
    }
}

const exportedObject = {
  set, get, getHeader, isTokenActive, remove, refresh, login, logout, switchUserRole, switchUser, refreshRole
};

export default exportedObject;
